import { ButtonWithLoader } from "@components/button_with_loader/button_with_loader";
import { HiddenField, MarkdownField, TextField } from "@components/fields";
import { FlashMessage } from "@components/flash_messages/flash_message";
import { useMutate } from "@hooks/use_mutate";
import { api } from "@services/api";
import { appRoutes } from "@services/app_routes";
import { prepareFormikInitialValues } from "@services/typed_helpers";
import { Form, Formik } from "formik";
import React, { FC, useMemo } from "react";

import * as types from "../../../types";

interface ContentBlockFormProps {
  contentBlock: types.ContentBlockInputValues;
}

const ContentBlockForm: FC<ContentBlockFormProps> = ({ contentBlock }) => {
  const apiCall = useMemo(() => {
    return (token: string) =>
      (contentBlockData?: types.ContentBlockInputValues) =>
        api.contentBlock.update(token, contentBlockData);
  }, []);

  const initialValues = useMemo(
    () => prepareFormikInitialValues(contentBlock),
    [contentBlock],
  );

  const {
    submitData: submit,
    loading,
    error: apiError,
    data,
  } = useMutate<
    types.ContentBlockUpdateResponse,
    types.ContentBlockInputValues
  >(apiCall);
  const { errors } = data ?? {};
  const handleSubmit = (values: types.ContentBlockInputValues) => {
    if (loading) {
      return;
    }
    submit(values).then((data) => {
      if (data?.success) {
        const currentUrl = new URL(window.location.href);
        currentUrl.pathname = appRoutes.admin.contentBlocks.index();

        window.location.replace(currentUrl);
      }
    });
  };

  const formatErrors = (errors: types.ApiModelError): string[] => {
    return Object.entries(errors).flatMap(([fieldName, errorWords]) => {
      return errorWords.map(
        (error) =>
          `${fieldName[0].toUpperCase() + fieldName.slice(1)} ${error}`,
      );
    });
  };

  return (
    <>
      {errors ? (
        <FlashMessage message={formatErrors(errors)} type="error" />
      ) : null}
      {apiError ? (
        <FlashMessage
          message="Something terrible has happened.  Please try again later."
          type="error"
        />
      ) : null}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        id="content_block_form"
      >
        <Form>
          <HiddenField id="contentBlockId" name="id" />

          <TextField
            name="page"
            label="Page"
            helpElement='e.g. "events", "home"'
            required
          />

          <TextField
            name="section"
            label="Section"
            helpElement='e.g. "announcement", "about"'
          />
          <MarkdownField name="body" label="Body" />
          <div className="admin-form-controls">
            <div className="admin-form-controls--button">
              <a
                role="button"
                href="/admin/content_blocks"
                className="secondary"
              >
                Cancel
              </a>
            </div>
            <div className="admin-form-controls--button">
              <ButtonWithLoader loading={loading} value="Submit" />
            </div>
          </div>
        </Form>
      </Formik>
    </>
  );
};

export { ContentBlockForm };
